import Image from 'next/image';
import Link from 'next/link';

// Shared, canonical article thumbnail card — the single source of typography,
// tag-chip styling/order and hover/touch behaviour for Blog and all five
// Resource areas. Areas map their data onto these props and only control layout
// (their own grid/block title) and visibility (which elements render).
//
// Structure: Image, Header, Subheader, Tag chips.
//
// Tag chips: up to `maxTags` (default 3) from the primary category first, then
// secondary, in priority order — matching Blog. There is no tertiary input, so
// tertiary can never display. The chip row always renders (even empty) so the
// spacing is preserved when an article has no tags.
//
// Pass `twoCol` for 2-col layouts (e.g. Related Content) to get the larger
// header/label typography. Pass `hero` for the Featured spotlight card to get
// the Blog Featured hero's larger title/meta and spacing. Both variants are
// defined in the shared SCSS — fonts, colours, chips, hover/touch and spacing
// cannot be changed per area.
const ArticleThumbnailCard = ({
  href = '/',
  target,
  image,
  imageAlt = 'article thumbnail',
  header,
  description,
  subheader,
  primaryCategory = null,
  secondaryCategories = [],
  maxTags = 3,
  onTagClick,
  twoCol = false,
  hero = false,
  showImage = true,
  showSubheader = true,
  showTags = true,
  imagePriority = false,
  className = '',
  onClick,
}) => {
  const tags = [primaryCategory, ...(secondaryCategories || [])]
    .filter(Boolean)
    .slice(0, maxTags);

  const src = image && String(image).trim() !== '' ? image : '/images/placeholder.png';

  return (
    <Link href={href || '/'} passHref>
      <a
        className={`article-thumbnail-card ${twoCol ? 'article-thumbnail-card--2col' : ''} ${hero ? 'article-thumbnail-card--hero' : ''} ${className}`.trim()}
        target={target}
        onClick={onClick}
      >
        {showImage && (
          <div className="article-thumbnail-card__image">
            <Image
              src={src}
              alt={imageAlt}
              layout="fill"
              priority={imagePriority}
              sizes="(max-width: 768px) 100vw, 33vw"
            />
          </div>
        )}

        {header ? (
          <div className="article-thumbnail-card__header">{header}</div>
        ) : null}

        {description ? (
          <div className="article-thumbnail-card__description">{description}</div>
        ) : null}

        {showSubheader && subheader ? (
          <div className="article-thumbnail-card__subheader">{subheader}</div>
        ) : null}

        {showTags && (
          <div className="article-thumbnail-card__tags">
            {tags.map((tag, index) => (
              <span
                key={`article-tag-${tag?.name || index}`}
                className="article-thumbnail-card__tag"
                onClick={
                  onTagClick
                    ? (event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onTagClick(event, tag);
                      }
                    : undefined
                }
              >
                {tag?.name}
              </span>
            ))}
          </div>
        )}
      </a>
    </Link>
  );
};

export default ArticleThumbnailCard;
